import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { CONFIG_DATE_FORMAT, DEFAULT_CONFIG_DATE_FORMAT } from '@/libs/constants/configs'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(duration)
dayjs.extend(relativeTime)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export const isValidDateFormat = (format: string): boolean => {
  return CONFIG_DATE_FORMAT.find(({ value }) => value === format) !== undefined
}

const utilities = (() => {
  let PRIVATE_FORMAT = DEFAULT_CONFIG_DATE_FORMAT
  return () => {
    const FORMAT_DATE_UTC = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
    const FORMAT_DATE_DEFAULT = PRIVATE_FORMAT
    const FORMAT_DATE_WITH_TIME = `${FORMAT_DATE_DEFAULT} HH:mm`
    const FORMAT_TIME = 'h:mm a'
    const FORMAT_TIME_DETAIL = 'HH:mm:ss A'
    const FORMAT_DATE_WITH_DAYS_OF_WEEK = `dd ${FORMAT_DATE_DEFAULT}`

    const formatType = {
      utc: FORMAT_DATE_UTC,
      default: FORMAT_DATE_DEFAULT,
      dateTime: FORMAT_DATE_WITH_TIME,
      time: FORMAT_TIME,
      timeDetail: FORMAT_TIME_DETAIL,
      dateWithDaysOfWeek: FORMAT_DATE_WITH_DAYS_OF_WEEK,
    }

    return {
      formatType,
      setDateFormat: (format: string) => {
        if (isValidDateFormat(format)) {
          PRIVATE_FORMAT = format
        }
      },
    }
  }
})()

export const CURRENT_DATE = new Date()

export const setDateFormat = (format: string) => utilities().setDateFormat(format)

export const getFormatType = () => utilities().formatType

export const formatDate = (
  dateParameter: string | Date | dayjs.Dayjs | null | undefined,
  outputFormat?: string,
  isStrictMode = false,
): string | undefined => {
  if (!dateParameter) {
    return undefined
  }

  const formatType = getFormatType()
  let date = dayjs(dateParameter, formatType.utc, isStrictMode)

  if (!date.isValid()) {
    date = dayjs(dateParameter)
  }

  if (date.isValid()) {
    return date.format(outputFormat || formatType.default)
  }

  return undefined
}

export const parseDateInstance = (
  dateParameter: string | Date | dayjs.Dayjs | null | undefined,
  format?: string,
): dayjs.Dayjs | null | undefined => {
  if (!dateParameter) {
    return null
  }
  const result = dayjs(dateParameter, format)
  if (result.isValid()) {
    return result
  }
  return undefined
}

export const parseDateToISOString = (
  date: string | Date | dayjs.Dayjs | null | undefined,
  dateFormat?: string,
): string => {
  if (date) {
    const dayjsDate = dayjs(date, dateFormat).toDate().toISOString()
    return dayjsDate
  }
  return ''
}

export const convertDateToISOString = (
  dateParameter: dayjs.Dayjs | null | undefined,
): string | undefined => dateParameter?.utc()?.toISOString()

export const createDateTimeInstance = (
  date: dayjs.Dayjs | null | undefined,
  time: dayjs.Dayjs | null | undefined,
): dayjs.Dayjs | null => {
  if (date) {
    return dayjs()
      .year(date.year())
      .month(date.month())
      .date(date.date())
      .hour(time?.hour() || 0)
      .minute(time?.minute() || 0)
      .second(0)
      .millisecond(0)
  }
  return null
}

export const dateUtils = {
  formatDate: (date: string | Date, format = 'DD/MM/YYYY'): string => {
    return dayjs(date).format(format)
  },

  formatDateTime: (
    date: string | Date,
    format = 'DD.MM.YYYY HH:mm',
  ): string => {
    return dayjs(date).format(format)
  },

  /**
   * Parse date with specific format
   * @param dateString - Date string to parse
   * @param format - Expected format
   */
  parseDate: (dateString: string, format: string) => {
    return dayjs(dateString, format)
  },

  isValidDate: (
    date: string | Date | dayjs.Dayjs | null | undefined,
  ): boolean => {
    if (date === null || date === undefined) return false
    return dayjs(date).isValid()
  },

  /**
   * Get current timestamp
   */
  now: () => dayjs(),

  /**
   * Convert to UTC
   * @param date - Date to convert
   */
  toUTC: (date: string | Date) => dayjs(date).utc(),

  /**
   * Convert to ISO string (for API requests)
   * @param date - Date to convert
   */
  toISOString: (date: string | Date): string => {
    return dayjs(date).toISOString()
  },

  /**
   * Convert to specific timezone
   * @param date - Date to convert
   * @param timezone - Target timezone
   */
  toTimezone: (date: string | Date, timezone: string) => {
    return dayjs(date).tz(timezone)
  },

  /**
   * Subtract time period (for date ranges)
   * @param amount - Number to subtract
   * @param unit - Time unit ('day', 'month', 'year', etc.)
   */
  subtract: (amount: number, unit: dayjs.ManipulateType) => {
    return dayjs().subtract(amount, unit)
  },

  /**
   * Add time period
   * @param amount - Number to add
   * @param unit - Time unit ('day', 'month', 'year', etc.)
   */
  add: (amount: number, unit: dayjs.ManipulateType) => {
    return dayjs().add(amount, unit)
  },

  /**
   * Check if date is before another date
   * @param date1 - First date
   * @param date2 - Second date
   */
  isBefore: (date1: string | Date, date2: string | Date): boolean => {
    return dayjs(date1).isBefore(dayjs(date2))
  },

  /**
   * Check if date is after another date
   * @param date1 - First date
   * @param date2 - Second date
   */
  isAfter: (date1: string | Date, date2: string | Date): boolean => {
    return dayjs(date1).isAfter(dayjs(date2))
  },

  /**
   * Get relative time display (e.g., "2 hours ago")
   * @param date - Date to compare
   */
  fromNow: (date: string | Date): string => {
    return dayjs(date).fromNow()
  },

  /**
   * Get end of current day (for date range validation)
   */
  endOfDay: () => dayjs().endOf('day'),

  /**
   * Check if date is same or before another date
   * @param date1 - First date
   * @param date2 - Second date
   */
  isSameOrBefore: (date1: string | Date, date2: string | Date): boolean => {
    return dayjs(date1).isSameOrBefore(dayjs(date2))
  },

  /**
   * Check if date is same or after another date
   * @param date1 - First date
   * @param date2 - Second date
   */
  isSameOrAfter: (date1: string | Date, date2: string | Date): boolean => {
    return dayjs(date1).isSameOrAfter(dayjs(date2))
  },

  /**
   * Create dayjs instance from date (for Ant Design compatibility)
   * @param date - Date to convert
   */
  create: (date?: string | Date) => {
    return date ? dayjs(date) : dayjs()
  },

  /**
   * Check if current date is after today (for disabling future dates)
   * @param current - Current date to check
   */
  isAfterToday: (
    current: string | Date | dayjs.Dayjs | null | undefined,
  ): boolean => {
    if (!current) return false
    return dayjs(current).isAfter(dayjs().endOf('day'))
  },

  /**
   * Convert date string with specific format to ISO string (replacement for convertDateToMomentISOString)
   * @param date - Date string to convert
   * @param dateFormat - Format of the input date string
   */
  convertToISOString: (date: string | Date, dateFormat?: string): string => {
    if (!date) return ''

    const parsed = dateFormat ? dayjs(date, dateFormat) : dayjs(date)
    return parsed.toISOString()
  },

  /**
   * Convert date to inclusive end date (adds 1 day for date range queries)
   * @param dateString - Date string to convert
   * @param dateFormat - Format of the input date string
   */
  toInclusiveDate: (dateString: string, dateFormat?: string): string => {
    if (!dateString) return ''

    const parsed = dateFormat
      ? dayjs(dateString, dateFormat)
      : dayjs(dateString)
    const inclusiveDate = parsed.add(1, 'day')
    return inclusiveDate.toISOString()
  },
}

export function isCurrentTimeInSAPDowntime(
  startTime: string | Date | dayjs.Dayjs,
  endTime: string | Date | dayjs.Dayjs,
): boolean {
  const currentTime = dayjs()
  const convertedStartTime = parseDateInstance(startTime)
  const convertedEndTime = parseDateInstance(endTime)

  return (
    currentTime.isAfter(convertedStartTime)
    && currentTime.isBefore(convertedEndTime)
  )
}

export { default as dayjs } from 'dayjs'
